import { Calendra2 } from "@common/DatePicker";
import Modal from "@common/Modal";
import { MAX_ADULT_AGE } from "@src/config/constant";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

type TravellersDobPopupProps = {
    isOpen: boolean;
    onClose: () => void;
    maxTravellerCount: number;
    travellersCount: number;
    travellerDobValues: any[];
    onSave: (travellersCount: number, travellerDobValues: any[]) => void;
};

const normalizeDobValues = (dobValues: any[] = [], count: number) => (
    Array.from({ length: count }, (_item, index) => dobValues[index] || "")
);

const getTravellerDobLabel = (index: number) => (
    index === 0 ? "DOB of Primary Traveller" : `DOB of Traveller ${index + 1}`
);

const MIN_TRAVELLER_AGE_DAYS = 14;

const getAdultDobMinDate = () => moment().subtract(MAX_ADULT_AGE + 1, "years").add(1, "day").startOf("day").toDate();

const getAdultDobMaxDate = () => moment().subtract(MIN_TRAVELLER_AGE_DAYS, "days").endOf("day").toDate();

const isAdultDobValid = (dobValue: any) => {
    if (!dobValue || !moment(dobValue).isValid()) return false;

    return moment(dobValue).isBetween(getAdultDobMinDate(), getAdultDobMaxDate(), "day", "[]");
};

export default function TravellersDobPopup({
    isOpen,
    onClose,
    maxTravellerCount,
    travellersCount,
    travellerDobValues,
    onSave,
}: TravellersDobPopupProps) {
    const safeMaxTravellerCount = Math.max(Number(maxTravellerCount) || 1, 1);
    const safeTravellersCount = Math.min(Math.max(Number(travellersCount) || 1, 1), safeMaxTravellerCount);
    const [selectedCount, setSelectedCount] = useState(safeTravellersCount);
    const [dobValues, setDobValues] = useState<any[]>(() => normalizeDobValues(travellerDobValues, safeTravellersCount));
    const [errorMessage, setErrorMessage] = useState("");
    const adultDobMinDate = useMemo(() => getAdultDobMinDate(), []);
    const adultDobMaxDate = useMemo(() => getAdultDobMaxDate(), []);

    const travellerOptions = useMemo(() => (
        Array.from({ length: safeMaxTravellerCount }, (_item, index) => index + 1)
    ), [safeMaxTravellerCount]);

    useEffect(() => {
        if (!isOpen) return;

        setSelectedCount(safeTravellersCount);
        setDobValues(normalizeDobValues(travellerDobValues, safeTravellersCount));
        setErrorMessage("");
        document.body.classList.add("overflow-hidden");

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };

        document.addEventListener("keydown", handleEscape);

        return () => {
            document.body.classList.remove("overflow-hidden");
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose, safeTravellersCount, travellerDobValues]);

    const handleCountSelect = (count: number) => {
        setSelectedCount(count);
        setDobValues((currentValues) => normalizeDobValues(currentValues, count));
        setErrorMessage("");
    };

    const handleDobChange = (index: number, date: Date) => {
        setDobValues((currentValues) => currentValues.map((value, dobIndex) => (
            dobIndex === index ? date : value
        )));
        setErrorMessage("");
    };

    const handleSave = () => {
        const nextDobValues = normalizeDobValues(dobValues, selectedCount);
        if (nextDobValues.some((value) => !value)) {
            setErrorMessage("Please select date of birth for all travellers");
            return;
        }

        if (nextDobValues.some((value) => !isAdultDobValid(value))) {
            setErrorMessage(`Traveller age should be between ${MIN_TRAVELLER_AGE_DAYS} days and ${MAX_ADULT_AGE} years`);
            return;
        }

        onSave(selectedCount, nextDobValues);
        onClose();
    };

    return (
        <div className="model-outer travel-feature-included-popup travel-travellers-dob-popup">
            <Modal show={isOpen} handleClose={onClose}>
                <div className="travel-feature-popup">
                    <div className="travel-feature-popup-head">
                        <h2 className="heading-h2">Select no. of travellers</h2>
                    </div>

                    <div className="travel-feature-popup-body">
                        <div className="travel-traveller-count-options">
                            {travellerOptions.map((count) => (
                                <button
                                    type="button"
                                    className={selectedCount === count ? "active" : ""}
                                    key={`traveller-count-${count}`}
                                    onClick={() => handleCountSelect(count)}
                                >
                                    {count}
                                </button>
                            ))}
                        </div>

                        <div className="travel-traveller-dob-grid">
                            {dobValues.map((dobValue, index) => (
                                <fieldset className="form-filed date-picker-detail" key={`traveller-dob-${index}`}>
                                    <div className={`material animation-effect${dobValue ? " active-label" : ""}`}>
                                        <Calendra2
                                            onChange={(date: Date) => handleDobChange(index, date)}
                                            placeholder={getTravellerDobLabel(index)}
                                            selected={dobValue}
                                            dateFormat="dd-MM-yyyy"
                                            fieldLabel="DOB"
                                            fieldname={`traveller_dob_${index}`}
                                            minDate={adultDobMinDate}
                                            maxDate={adultDobMaxDate}
                                            popperPlacement="bottom-start"
                                            popperClassName="travel-dob-datepicker-popper"
                                        />
                                    </div>
                                </fieldset>
                            ))}
                        </div>

                        {errorMessage ? <span className="error-txt travel-traveller-dob-error">{errorMessage}</span> : null}
                    </div>

                    <div className="travel-feature-popup-footer">
                        <button type="button" className="btn-primary" onClick={handleSave}>
                            Save
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
